import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { ArrowLeft, Save, RotateCcw, Eye, EyeOff, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TEMPLATE_TYPES = {
  welcome: 'Boas-vindas',
  payment_confirmed: 'Pagamento Confirmado',
  payment_failed: 'Falha no Pagamento',
  account_deleted: 'Conta Excluída',
  password_reset: 'Recuperação de Senha'
};

const AdminEmailTemplates = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [selectedType, setSelectedType] = useState('welcome');
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (templates.length > 0) {
      loadTemplate(selectedType);
    }
  }, [selectedType, templates]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/email/templates`,
        getAuthHeaders()
      );
      setTemplates(response.data.templates);
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
      alert('Erro ao carregar templates de email');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplate = (type) => {
    const template = templates.find(t => t.template_type === type);
    if (template) {
      setCurrentTemplate(template);
    }
  };

  const handleSave = async () => {
    if (!currentTemplate) return;

    setSaving(true);

    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/email/templates/${currentTemplate.template_type}`,
        {
          subject: currentTemplate.subject,
          html_content: currentTemplate.html_content
        },
        getAuthHeaders()
      );
      alert('Template salvo com sucesso!');
      fetchTemplates(); // Recarregar templates
    } catch (error) {
      console.error('Erro ao salvar template:', error);
      alert('Erro ao salvar template');
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async () => {
    if (!currentTemplate) return;

    if (!confirm('Tem certeza que deseja restaurar este template para o padrão? As alterações serão perdidas.')) {
      return;
    }

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/email/templates/${currentTemplate.template_type}/restore`,
        {},
        getAuthHeaders()
      );
      alert('Template restaurado para o padrão!');
      setCurrentTemplate(response.data);
      fetchTemplates(); // Recarregar templates
    } catch (error) {
      console.error('Erro ao restaurar template:', error);
      alert('Erro ao restaurar template');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#00A86B]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">
              Templates de Email
            </h1>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Template List */}
          <Card className="p-4 h-fit">
            <h2 className="font-semibold text-lg mb-4">Tipos de Email</h2>
            <div className="space-y-2">
              {Object.entries(TEMPLATE_TYPES).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setSelectedType(key)}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                    selectedType === key
                      ? 'bg-[#00A86B] text-white'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  <div className="font-medium">{label}</div>
                  {templates.find(t => t.template_type === key)?.is_default === false && (
                    <div className="text-xs mt-1 opacity-80">Customizado</div>
                  )}
                </button>
              ))}
            </div>
          </Card>

          {/* Template Editor */}
          <div className="lg:col-span-2 space-y-6">
            {currentTemplate && (
              <>
                <Card className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="font-semibold text-lg">
                      {TEMPLATE_TYPES[currentTemplate.template_type]}
                    </h2>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowPreview(!showPreview)}
                        className="flex items-center gap-2"
                      >
                        {showPreview ? (
                          <>
                            <EyeOff className="w-4 h-4" />
                            Ocultar Preview
                          </>
                        ) : (
                          <>
                            <Eye className="w-4 h-4" />
                            Ver Preview
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {/* Subject */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Assunto
                      </label>
                      <Input
                        type="text"
                        value={currentTemplate.subject}
                        onChange={(e) =>
                          setCurrentTemplate({
                            ...currentTemplate,
                            subject: e.target.value
                          })
                        }
                        className="w-full"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Você pode usar variáveis como: {'{{customer_name}}'}, {'{{order_id}}'}, {'{{project_name}}'}
                      </p>
                    </div>

                    {/* HTML Content */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Conteúdo HTML
                      </label>
                      <Textarea
                        value={currentTemplate.html_content}
                        onChange={(e) =>
                          setCurrentTemplate({
                            ...currentTemplate,
                            html_content: e.target.value
                          })
                        }
                        rows={20}
                        className="w-full font-mono text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Use HTML para estilizar o email. Variáveis disponíveis: {'{{customer_name}}'}, {'{{order_id}}'}, {'{{project_name}}'}, {'{{reset_link}}'}
                      </p>
                    </div>

                    {/* Status Badge */}
                    {currentTemplate.is_default !== undefined && (
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            currentTemplate.is_default
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {currentTemplate.is_default ? '📋 Template Padrão' : '✏️ Template Customizado'}
                        </span>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-4">
                      <Button
                        onClick={handleRestore}
                        variant="outline"
                        className="flex items-center gap-2"
                        disabled={saving}
                      >
                        <RotateCcw className="w-4 h-4" />
                        Restaurar Padrão
                      </Button>

                      <Button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-2 bg-[#00A86B] hover:bg-[#00A86B]/90"
                      >
                        {saving ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                        {saving ? 'Salvando...' : 'Salvar Alterações'}
                      </Button>
                    </div>
                  </div>
                </Card>

                {/* Preview */}
                {showPreview && (
                  <Card className="p-6">
                    <h3 className="font-semibold text-lg mb-4">Preview</h3>
                    <div className="border rounded-lg p-4 bg-white overflow-auto max-h-[600px]">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: currentTemplate.html_content
                            .replace(/{{customer_name}}/g, 'João Silva')
                            .replace(/{{order_id}}/g, 'ABC12345')
                            .replace(/{{project_name}}/g, 'Casa Moderna 150m²')
                            .replace(/{{reset_link}}/g, '#')
                            .replace(/{{items_table}}/g, '<table><tr><td>Item exemplo</td></tr></table>')
                            .replace(/{{documents_section}}/g, '')
                        }}
                      />
                    </div>
                  </Card>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminEmailTemplates;
